"use client";
import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Divider, Chip, Spinner } from "@nextui-org/react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { usersAPI, clientsAPI } from "../services/api";

import DefaultLayout from "@/layouts/default";
import { formatDate } from "@/utils/dateUtils";

interface Client {
  id: string;
  name: string;
  subdomain: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  clientId?: string;
  client_id?: string;
  created_at: string;
  updated_at: string;
}

interface UserLog {
  id: number;
  action: string;
  description: string;
  performed_by: {
    id: number;
    name: string;
    email: string;
  } | null;
  target_user: {
    id: number;
    name: string;
    email: string;
  } | null;
  ip_address: string;
  user_agent: string;
  details: any;
  created_at: string;
  formatted_date: string;
  time_ago: string;
}

const UserViewPage: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [logs, setLogs] = useState<UserLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [currentUserRole, _setCurrentUserRole] = useState<string>(
    localStorage.getItem("role") || ""
  );
  const [currentUserClientId, _setCurrentUserClientId] = useState<string | null>(
    localStorage.getItem("clientId") || null
  );
  const [clientInfo, setClientInfo] = useState<Client | null>(null);
  const isSuperAdmin = currentUserRole === "superadmin";
  const navigate = useNavigate();

  // Check if current user has permission to view this user
  const checkViewPermissions = async () => {
    // If current user is a superadmin, they can view any user
    if (isSuperAdmin) {
      return true;
    }

    // Tenant admins can only view users in their own client
    if (currentUserRole === "admin" && currentUserClientId) {
      try {
        const userData = await usersAPI.getById(userId || "");
        
        // Convert both to strings for comparison to avoid type mismatch
        const userClientId = String(userData.clientId || userData.client_id || "");
        const currentClientId = String(currentUserClientId);
        
        console.log("View permission check:", {
          currentUserRole,
          currentClientId,
          userClientId,
          userData,
          hasPermission: userClientId === currentClientId
        });

        // Check if the user belongs to the same client as the admin
        return userClientId === currentClientId;
      } catch (error) {
        console.error("Error checking view permissions:", error);
        return false;
      }
    }

    // Other roles can't view users
    return false;
  };

  // Fetch user logs
  const fetchUserLogs = async () => {
    if (!userId) return;
    
    setLogsLoading(true);
    try {
      const response = await fetch(`http://127.0.0.1:8001/api/user-logs/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      } else {
        console.error('Failed to fetch user logs:', response.status);
      }
    } catch (error) {
      console.error('Error fetching user logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        setError(t("User ID is not provided."));
        setPermissionLoading(false);
        return;
      }

      setLoading(true);
      try {
        const userData = await usersAPI.getById(userId);
        setUser(userData);

        // If the user belongs to a client, fetch the client information
        if (userData.clientId || userData.client_id) {
          try {
            const clientId = userData.clientId || userData.client_id;
            const client = await clientsAPI.getById(clientId);
            setClientInfo(client);
          } catch (err) {
            console.error("Error fetching client:", err);
          }
        }

        // Check permissions after user data is loaded
        const hasPermission = await checkViewPermissions();
        if (!hasPermission) {
          setError(t("You don't have permission to view this user."));
        } else {
          // Fetch user logs if permission granted
          fetchUserLogs();
        }
        setPermissionLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError(t("Failed to fetch user data."));
        setPermissionLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, t]);

  const handleDelete = async () => {
    if (!userId) return;
    try {
      const confirmDelete = window.confirm(
        t("Are you sure you want to delete this user?"),
      );

      if (!confirmDelete) return;

      setLoading(true);
      await usersAPI.delete(userId);
      alert(t("User deleted successfully!"));
      setLoading(false);
      navigate("/users");
    } catch (error: any) {
      console.error("Error deleting user:", error);
      setLoading(false);
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.error || error.message || "Unknown error";
      alert(t("Failed to delete user: ") + errorMessage);
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'view':
        return 'primary';
      case 'edit':
        return 'warning';
      case 'delete':
        return 'danger';
      case 'login':
        return 'success';
      case 'logout':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading || permissionLoading) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <p className="text-center">{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <div className="bg-red-500 text-white p-4 rounded mb-4">{error}</div>
          <Button color="primary" onClick={() => navigate("/users")}>
            {t("Back to Users")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  if (!user) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <p className="text-center">{t("User not found")}</p>
          <Button
            className="mt-4"
            color="primary"
            onClick={() => navigate("/users")}
          >
            {t("Back to Users")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="container mx-auto p-4 max-w-6xl">
        {/* User Details Section */}
        <section className="flex flex-col items-center justify-center py-8 mb-8">
          <div className="max-w-lg w-full p-6 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
              {user.name}
            </h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("User ID")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {user.id}
                </span>
              </div>
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("Email")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {user.email}
                </span>
              </div>
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("Role")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {user.role}
                </span>
              </div>
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("Status")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {user.status || 'active'}
                </span>
              </div>
              {clientInfo && (
                <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {t("Client Organization")}:
                  </span>
                  <span className="text-gray-900 dark:text-gray-200">
                    {clientInfo.name}
                  </span>
                </div>
              )}
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("Created Date")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {formatDate(user.created_at)}
                </span>
              </div>
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("Last Updated")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {formatDate(user.updated_at)}
                </span>
              </div>
            </div>

            <div className="flex justify-center space-x-4 mt-8">
              {(currentUserRole === "admin" || currentUserRole === "superadmin") && (
                <>
                  <Button
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                    onClick={() => navigate(`/edit-user/${userId}`)}
                  >
                    {t("Edit")}
                  </Button>
                  <Button
                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
                    onClick={handleDelete}
                  >
                    {t("Delete")}
                  </Button>
                </>
              )}
              <Button
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                onClick={() => navigate("/users")}
              >
                {t("Back")}
              </Button>
            </div>
          </div>
        </section>

        {/* Activity Logs Section */}
        <section className="mb-8">
          <Card className="w-full">
            <CardHeader className="flex gap-3 justify-between">
              <div className="flex flex-col">
                <p className="text-md font-semibold">{t("Activity Logs")}</p>
                <p className="text-small text-default-500">
                  {t("Recent activity for this user")}
                </p>
              </div>
              {(currentUserRole === "admin" || currentUserRole === "superadmin") && (
                <Button
                  color="primary"
                  variant="flat"
                  size="sm"
                  onClick={() => navigate("/logs")}
                >
                  {t("View All Logs")}
                </Button>
              )}
            </CardHeader>
            <Divider/>
            <CardBody>
              {logsLoading ? (
                <div className="flex justify-center p-4">
                  <Spinner size="md" />
                  <span className="ml-2">{t("Loading logs...")}</span>
                </div>
              ) : logs.length > 0 ? (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div 
                      key={log.id} 
                      className="flex items-start justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Chip 
                            color={getActionColor(log.action)} 
                            size="sm" 
                            variant="flat"
                          >
                            {log.action}
                          </Chip>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {log.description}
                          </span>
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                          {log.performed_by && (
                            <p>
                              <strong>{t("Performed by")}:</strong> {log.performed_by.name} ({log.performed_by.email})
                            </p>
                          )}
                          <p>
                            <strong>{t("Time")}:</strong> {log.formatted_date} ({log.time_ago})
                          </p>
                          {log.ip_address && (
                            <p>
                              <strong>{t("IP Address")}:</strong> {log.ip_address}
                            </p>
                          )}
                        </div>
                        
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 text-xs">
                            <details className="text-gray-600 dark:text-gray-300">
                              <summary className="cursor-pointer hover:text-gray-800 dark:hover:text-gray-100">
                                {t("Details")}
                              </summary>
                              <pre className="mt-1 bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs overflow-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>{t("No activity logs found for this user.")}</p>
                </div>
              )}
            </CardBody>
          </Card>
        </section>
      </div>
    </DefaultLayout>
  );
};

export default UserViewPage; 